/**
 * Pure, Google-free helpers for sorting and filtering the site list.
 * Relies on distanceInMetersBetween from coordsHelpers (which falls back
 * to the haversine formula when the Google Maps geometry lib is unavailable).
 */
import { SiteListObject } from "~/entities/siteEntities";
import { OrderFulfillmentType, OrderSource } from "~/entities/orderEntities";
import { distanceInMetersBetween } from "~/helpers/coordsHelpers";

export type LatLng = { lat: number; lng: number };

/**
 * Short capability tags shown on a store row so the list carries useful info
 * at a glance — what the store can do beyond plain pickup. Derived purely from
 * the store's active order types + opening hours:
 *   - "Delivery"  → has an active DELIVERY order type
 *   - "Dine-in"   → has an active IN_STORE order type
 *   - "Open late" → open until 10pm or later today (or 24h)
 * Pickup itself is implied by the pickup list, so it isn't tagged.
 */
/**
 * Whether a site's coordinates are configured on its record (admin: site address -> latitude/longitude).
 *
 * The storefront never geocodes a store to fill them in: that was a billable Google request per site per mount. A
 * site without them is still listed and can be ordered from; it just has no distance, so it sorts after the sites
 * that do and shows no distance label.
 */
export function hasCoordinates(site: SiteListObject): boolean {
  return site.address.latitude != null && site.address.longitude != null;
}

export function storeCapabilityTags(
  site: SiteListObject,
  orderSource?: OrderSource,
): string[] {
  const tags: string[] = [];
  // With an experience given, only its order types count: catering delivery doesn't make a store "Delivery" for
  // everyday ordering
  const active = new Set(
    (site.orderTypes ?? [])
      .filter((o) => o.active && (!orderSource || o.source === orderSource))
      .map((o) => o.fulfillmentType),
  );
  if (active.has(OrderFulfillmentType.DELIVERY)) tags.push("Delivery");
  if (active.has(OrderFulfillmentType.IN_STORE)) tags.push("Dine-in");

  // "Open late": any opening block today that closes at 22:00+ (or runs to
  // midnight / is 24h). availability uses hour 24 (or 0 with hour-0 start) for
  // round-the-clock.
  const availability = site.openingTimes?.[0]?.availability ?? [];
  const openLate = availability.some((a) => {
    const end = a.endsAt?.hour ?? 0;
    const start = a.startsAt?.hour ?? 0;
    const allDay = start === 0 && (end === 24 || end === 0);
    return allDay || end >= 22 || end === 24;
  });
  if (openLate) tags.push("Open late");

  return tags;
}

/**
 * Sort sites by straight-line distance from `center`.
 * Sites without lat/lng coordinates are placed at the END of the list.
 * Does NOT mutate the original array.
 */
export function sortByDistance(
  sites: SiteListObject[],
  center: LatLng,
): SiteListObject[] {
  return [...sites].sort((a, b) => {
    const aHasCoords =
      a.address.latitude != null && a.address.longitude != null;
    const bHasCoords =
      b.address.latitude != null && b.address.longitude != null;

    if (!aHasCoords && !bHasCoords) return 0;
    if (!aHasCoords) return 1;
    if (!bHasCoords) return -1;

    const distA = distanceInMetersBetween(
      center.lat,
      center.lng,
      a.address.latitude!,
      a.address.longitude!,
    );
    const distB = distanceInMetersBetween(
      center.lat,
      center.lng,
      b.address.latitude!,
      b.address.longitude!,
    );

    return distA - distB;
  });
}

/**
 * The region/group a site belongs to. Prefers the group the admin assigns in
 * Site Management (the first non-empty `organisationPath` element, e.g.
 * "North Island"), falling back to the address city. Trimmed because the admin
 * data can carry trailing whitespace ("North Island "). Returns undefined when
 * a site is ungrouped and has no city, so callers can bucket it as "Other".
 */
export function siteRegion(site: SiteListObject): string | undefined {
  const group = site.organisationPath?.map((p) => p?.trim()).find(Boolean);
  return group || site.address.city?.trim() || undefined;
}

/**
 * Group sites by their region (admin group, else city). Ungrouped sites with no
 * city fall into the "Other" bucket.
 */
export function groupByRegion(
  sites: SiteListObject[],
): Record<string, SiteListObject[]> {
  return sites.reduce<Record<string, SiteListObject[]>>((acc, site) => {
    const key = siteRegion(site) ?? "Other";
    return { ...acc, [key]: [...(acc[key] ?? []), site] };
  }, {});
}

/** Minimum store count before a *city-derived* list is worth splitting into regions. */
const REGION_GROUP_MIN = 7;

/** True when at least one site has an admin-assigned group (organisationPath). */
export function hasExplicitGroups(sites: SiteListObject[]): boolean {
  return sites.some((s) => s.organisationPath?.some((p) => p?.trim()));
}

export type PickupView =
  | { mode: "flat"; sites: SiteListObject[] }
  | { mode: "grouped"; groups: { region: string; sites: SiteListObject[] }[] };

/**
 * Decide how to present the pickup list. A long list (e.g. a 60-store chain)
 * is split into alphabetical region headings — like BurgerFuel/Sal's. But when
 * the user is actively narrowing down (typing a search, or sorting by their own
 * location), a flat relevance-ordered list beats region buckets, so we collapse
 * back to flat. A short list, or one with a single region, also stays flat.
 *
 * `sites` is assumed already filtered/sorted by the caller; group order is
 * alphabetical by region and each region preserves the incoming order.
 */
export function buildPickupView(
  sites: SiteListObject[],
  { searching, located }: { searching: boolean; located: boolean },
): PickupView {
  // While the user is narrowing down (search / locate), a flat relevance list
  // always beats region buckets.
  if (searching || located) {
    return { mode: "flat", sites };
  }

  // The ≥7 threshold only guards *city-derived* grouping (don't bucket a handful
  // of stores by city). When the admin has explicitly grouped sites, honour that
  // intent at any list size — that's the whole point of creating the groups.
  if (!hasExplicitGroups(sites) && sites.length < REGION_GROUP_MIN) {
    return { mode: "flat", sites };
  }

  const grouped = groupByRegion(sites);
  const groups = Object.keys(grouped)
    .sort((a, b) => a.localeCompare(b))
    .map((region) => ({ region, sites: grouped[region] }));

  // A single *city-derived* region isn't worth a heading — but a single explicit
  // admin group still is (it's a real, named region the operator chose to show).
  if (groups.length < 2 && !hasExplicitGroups(sites)) {
    return { mode: "flat", sites };
  }

  return { mode: "grouped", groups };
}

export type FilterOptions = {
  search?: string;
  region?: string | null;
  orderType?: string | null;
};

/**
 * Filter sites by free-text search, region, and/or order-type code.
 * All supplied filters are ANDed together; omitting a filter means "all".
 */
export function filterSites(
  sites: SiteListObject[],
  { search, region, orderType }: FilterOptions,
): SiteListObject[] {
  return sites.filter((site) => {
    // Text search across name + address fields
    if (search && search.trim()) {
      const haystack = [
        site.name,
        site.address.address1,
        site.address.city,
        site.address.postalCode,
        site.address.state,
        ...(site.organisationPath ?? []),
      ]
        .join(", ")
        .toLowerCase();
      if (!haystack.includes(search.toLowerCase().trim())) return false;
    }

    // Region filter (admin group, else city) — matches the grouping key.
    if (region && siteRegion(site) !== region) return false;

    // Order-type filter
    if (orderType && !site.orderTypes.some((ot) => ot.code === orderType))
      return false;

    return true;
  });
}
